import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, ShieldCheck, HandHeart, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/data/content";
import { CONTACT, AVAILABILITY } from "@/data/site";
import { Seo } from "@/components/Seo";

// Booking slots are generated from AVAILABILITY in src/data/site.js so the
// calendar always matches the advertised hours (single source of truth).
function label12h(hour24) {
  const period = hour24 >= 12 ? "PM" : "AM";
  const h = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${h}:00 ${period}`;
}

const TIMES = (() => {
  const out = [];
  for (let h = AVAILABILITY.startHour; h <= AVAILABILITY.endHour; h += AVAILABILITY.slotIntervalHours) {
    out.push(label12h(h));
  }
  return out;
})();

function nextDays(n) {
  const out = [];
  // Anchor "today" to the business timezone so dates/weekdays are correct for
  // every visitor regardless of their own timezone (Intl handles NZ daylight saving).
  const tzNow = new Date(new Date().toLocaleString("en-US", { timeZone: AVAILABILITY.timezone }));
  const d = new Date(tzNow);
  while (out.length < n) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (AVAILABILITY.includeWeekends || (day !== 0 && day !== 6)) out.push(new Date(d));
  }
  return out;
}

export default function Booking() {
  const days = nextDays(AVAILABILITY.daysToShow);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time || !form.service) { toast.error("Please choose a service, date and time."); return; }
    setLoading(true);
    try {
      await api.post("/booking", form);
      setDone(true);
      toast.success("Consultation requested!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 pb-20">
        <div className="max-w-lg mx-auto px-5 sm:px-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="booking-success">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto"><Check size={32} /></span>
            <h1 className="mt-6 font-display text-3xl font-semibold text-[#0B3D2E]">Consultation requested!</h1>
            <p className="mt-3 text-slate-600">Thanks {form.name.split(" ")[0]}. We've received your request for <strong>{form.date}</strong> at <strong>{form.time}</strong>. A licensed adviser will confirm shortly by email.</p>
            <Link to="/"><Button className="mt-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-6">Back to home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-28 pb-20">
      <Seo
        title="Book a Free Insurance Consultation | Quantum Cover"
        description="Book a free, no-obligation chat with a licensed adviser. Clear insurance help across New Zealand, in your language if you need it."
        path="/booking"
      />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8">
        <div>
          <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-medium mb-3"><CalendarDays size={16} /> Free Consultation</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0B3D2E] tracking-tight">Book your free chat</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">A relaxed, no-obligation chat with a licensed adviser. We'll listen, answer whatever's on your mind, and help you get your head around your options. No jargon, no pushiness.</p>
          <ul className="mt-7 space-y-3">
            {[
              { icon: Clock, t: "Around 30 minutes, at a time that suits you" },
              { icon: HandHeart, t: "No pressure at all. We explain things first, then it's your call" },
              { icon: ShieldCheck, t: "Advice via our licensed partner, Coversure" },
            ].map((x) => (
              <li key={x.t} className="flex items-start gap-3 text-slate-700"><span className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 shrink-0"><x.icon size={16} /></span>{x.t}</li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-7 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-sm">Full name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name" className="mt-1.5 rounded-xl h-12" /></div>
            <div><Label className="text-sm">Phone</Label><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" className="mt-1.5 rounded-xl h-12" /></div>
          </div>
          <div><Label className="text-sm">Email</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" className="mt-1.5 rounded-xl h-12" /></div>
          <div>
            <Label className="text-sm">What's it about?</Label>
            <Select value={form.service} onValueChange={(v) => set("service", v)}>
              <SelectTrigger className="mt-1.5 rounded-xl h-12" data-testid="booking-service"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}
                <SelectItem value="General enquiry">General enquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Preferred date</Label>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="booking-availability-note">Available {CONTACT.hoursDays} · {CONTACT.hoursTime}</p>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {days.map((d) => {
                const label = d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });
                const val = d.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
                return (
                  <button type="button" key={val} onClick={() => set("date", val)} data-testid="booking-date-option"
                    className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors ${form.date === val ? "bg-[#0B3D2E] text-white border-[#0B3D2E]" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-sm">Preferred time</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {TIMES.map((t) => (
                <button type="button" key={t} onClick={() => set("time", t)} data-testid="booking-time-option"
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${form.time === t ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div><Label className="text-sm">Anything else? (optional)</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes" className="mt-1.5 rounded-xl" rows={2} /></div>
          <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base">{loading ? "Booking…" : "Confirm my free consultation"}</Button>
        </form>
      </div>
    </div>
  );
}
